
import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Clock, Flame, CheckCircle2, Utensils } from 'lucide-react';
import FaitImage from './FaitImage';
import FadeIn from './FadeIn';
import { useLanguage } from './LanguageContext';

const MenuItemDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t, isRtl } = useLanguage();
  const navigate = useNavigate();
  
  // Forward action arrow (points forward in reading direction)
  const ArrowIcon = isRtl ? ArrowLeft : ArrowRight;
  
  // Back action arrow (points backward in reading direction)
  const BackIcon = isRtl ? ArrowRight : ArrowLeft; 

  // Find the item from translations
  const item = t.menu.items.find((i) => i.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!item) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-fait-dark text-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">המנה לא נמצאה</h2>
          <Link to="/menu" className="text-fait-gold hover:underline">חזרה לתפריט</Link>
        </div>
      </div>
    );
  }

  // Image logic - Updated for new IDs
  const getProductImage = (id: string) => {
    if (id.includes('cholent')) return '/images/hero-cholent.jpg';
    if (id.includes('yapchik')) return '/images/hero-cholent.jpg'; // Reusing cholent image for yapchik meat context
    if (id.includes('kugel')) return '/images/kugel-yerushalmi.jpg';
    if (id.includes('liver') || id.includes('egg') || id.includes('dip') || id.includes('arbes') || id.includes('dessert')) return '/images/cholent-plate.jpg';
    if (id.includes('drink') || id.includes('water') || id.includes('bottle') || id.includes('soda')) return '/images/atmosphere-interior.jpg';
    
    return '/images/hero-cholent.jpg';
  };

  // "Extra Details" based on new ID structure
  const getExtraDetails = (id: string) => {
    const isHe = isRtl;
    
    // MAINS
    if (id.includes('cholent')) {
        return {
          prepTime: isHe ? 'בישול איטי' : 'Slow Cooked',
          ingredients: isHe 
            ? ['בשר בקר משובח', 'תפוחי אדמה מזן מובחר', 'שעועית לבנה ואדומה', 'גריסים', 'קישקע']
            : ['Premium Beef', 'Selected Potatoes', 'White & Red Beans', 'Groats', 'Kishke'],
          highlight: isHe ? 'הסוד הוא בבישול האיטי ובתיבול המסורתי. מנה עשירה, סמיכה ומנחמת שאי אפשר להפסיק לאכול.' : 'The secret lies in the slow cooking and traditional seasoning.'
        };
    }
    
    if (id.includes('yapchik')) {
        return {
          prepTime: isHe ? 'נאפה כל הלילה' : 'Baked All Night',
          ingredients: isHe 
            ? ['תפוחי אדמה מגורדים', 'בשר בקר שומני ורך', 'תבלינים מיוחדים']
            : ['Grated Potatoes', 'Fatty Beef', 'Special Spices'],
          highlight: isHe ? 'השילוב המושלם והמושחת בין קוגל תפוחי אדמה לבשר עסיסי שנמס בפה.' : 'The perfect sinful combo of potato kugel and juicy meat.'
        };
    }

    // SIDES - Specific Logic
    if (id.includes('liver')) {
        return {
            prepTime: isHe ? 'הכנה יומית טרייה' : 'Fresh Daily',
            ingredients: isHe 
              ? ['כבד עוף צלוי', 'המון בצל מטוגן', 'ביצים', 'תבליני הבית']
              : ['Roasted Chicken Liver', 'Fried Onions', 'Eggs', 'House Spices'],
            highlight: isHe ? 'מרקם גס ואותנטי, כמו שסבתא הייתה מכינה. הולך מעולה עם חלה טרייה.' : 'Coarse authentic texture, just like grandma used to make.'
        };
    }

    if (id.includes('egg_salad')) {
        return {
            prepTime: isHe ? 'הכנה במקום' : 'Freshly Made',
            ingredients: isHe 
              ? ['ביצים טריות', 'מיונז איכותי', 'תיבול עדין']
              : ['Fresh Eggs', 'Quality Mayo', 'Seasoning'],
            highlight: isHe ? 'סלט ביצים קלאסי בצבע זהוב משגע, עם טעם של בית שמשתלב נהדר ליד הצ\'ולנט.' : 'Classic golden egg salad, tastes like home.'
        };
    }

    if (id.includes('tomato_dip')) {
        return {
            prepTime: isHe ? 'נסחט טרי' : 'Freshly Squeezed',
            ingredients: isHe 
              ? ['עגבניות בשלות', 'שום טרי', 'שמן', 'פלפל חריף (במידה)', 'תבליני הבית']
              : ['Ripe Tomatoes', 'Fresh Garlic', 'Oil', 'Hot Pepper', 'House Spices'],
            highlight: isHe ? 'המטבל שפותח את התיאבון. רעננות של עגבניות שמאזנת את הכבדות של הבשר.' : 'Appetite opener. Freshness that balances the heavy meat.'
        };
    }

    if (id.includes('tahini')) {
        return {
            prepTime: isHe ? 'טחינה במקום' : 'Prepared on spot',
            ingredients: isHe 
              ? ['טחינה גולמית איכותית', 'לימון טרי', 'שום', 'פטרוזיליה קצוצה', 'מים קרים']
              : ['Raw Tahini', 'Fresh Lemon', 'Garlic', 'Parsley', 'Cold Water'],
            highlight: isHe ? 'טחינה סמיכה ואיכותית במרקם שמנת. החברה הכי טובה של החלה.' : 'Thick and creamy tahini. The Challah\'s best friend.'
        };
    }

    if (id.includes('schug')) {
        return {
            prepTime: isHe ? 'כתישה ביתית' : 'Home Crushed',
            ingredients: isHe 
              ? ['פלפלים ירוקים חריפים', 'כוסברה טרייה', 'שום', 'תבלינים סודיים', 'שמן']
              : ['Hot Green Peppers', 'Fresh Cilantro', 'Garlic', 'Secret Spices', 'Oil'],
            highlight: isHe ? 'לא לאמיצים בלבד, אבל בהחלט נותן בעיטה. חריף ירוק אמיתי שמשדרג כל ביס.' : 'A real spicy kick that upgrades every bite.'
        };
    }

    if (id.includes('arbes')) {
         return {
            prepTime: isHe ? 'בישול ארוך' : 'Boiled',
            ingredients: isHe 
              ? ['גרגירי חומוס שלמים', 'מלח', 'פלפל שחור גרוס (הרבה!)']
              : ['Chickpeas', 'Salt', 'Black Pepper'],
            highlight: isHe ? 'נשנוש מסורתי, בריא ופשוט שאי אפשר להפסיק לאכול. מוגש חם עם הרבה פלפל שחור.' : 'Traditional healthy snack, served warm with lots of black pepper.'
        };
    }

    if (id.includes('kugel_noodle')) {
        return {
            prepTime: isHe ? 'אפייה ארוכה' : 'Long Bake',
            ingredients: isHe 
              ? ['אטריות דקות', 'קרמל ביתי (סוכר ושמן)', 'פלפל שחור גרוס', 'ביצים']
              : ['Thin Noodles', 'Homemade Caramel', 'Black Pepper', 'Eggs'],
            highlight: isHe ? 'חריפות עדינה שמשתלבת עם המתיקות של הקרמל. קלאסיקה בני ברקית במיטבה.' : 'Subtle heat balancing the caramel sweetness.'
        };
    }

    if (id.includes('kugel_potato') || id.includes('kugel_overnight')) {
        return {
            prepTime: isHe ? 'אפייה בחום גבוה / כל הלילה' : 'High Heat / Overnight',
            ingredients: isHe 
              ? ['תפוחי אדמה מובחרים', 'בצל', 'ביצים', 'שמן', 'מלח ופלפל']
              : ['Potatoes', 'Onions', 'Eggs', 'Oil', 'Salt & Pepper'],
            highlight: isHe ? 'רך מבפנים, קריספי מבחוץ. הגרסה של האוברנייט מקבלת צבע חום עמוק וטעם עשיר במיוחד.' : 'Crispy outside, soft inside. Overnight version is deep brown and rich.'
        };
    }
    
    if (id.includes('dessert')) {
        return {
            prepTime: isHe ? 'מתוק וטרי' : 'Sweet & Fresh',
            ingredients: isHe 
              ? ['מתכון הבית', 'רכיבים איכותיים']
              : ['House Recipe', 'Quality Ingredients'],
            highlight: isHe ? 'הסיום המתוק והמושלם לארוחת צ\'ולנט עשירה.' : 'The perfect sweet ending to a rich Cholent meal.'
        };
    }

    // DRINKS - Generic but specific to beverages
    if (id.includes('drink') || id.includes('water') || id.includes('bottle') || id.includes('soda')) {
         return {
            prepTime: isHe ? 'מוגש קר כקרח' : 'Served Ice Cold',
            ingredients: isHe 
              ? ['בקבוק/פחית סגורה', 'כוס חד פעמית', 'קש (אם תבקשו)']
              : ['Sealed Bottle/Can', 'Cup', 'Straw'],
            highlight: isHe ? 'אין כמו שתייה צוננת ומוגזת כדי להוריד את האוכל ולרענן את הגרון אחרי הצ\'ולנט.' : 'Nothing like a cold fizzy drink to wash down the food.'
        };
    }
    
    // Fallback just in case
    return {
        prepTime: isHe ? 'הגשה מיידית' : 'Instant Serve',
        ingredients: [],
        highlight: isHe ? 'התוספת המרעננת והמושלמת לארוחה.' : 'The perfect refreshing addition.'
    };
  };

  const extras = getExtraDetails(item.id);

  return (
    <div className="min-h-screen bg-[#120C08] pb-20 pt-28">
      {/* Navigation Bar Placeholder (visual consistency) */}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Back Button */}
        <FadeIn direction="right">
            <Link to="/menu" className="inline-flex items-center gap-2 text-gray-400 hover:text-fait-gold transition-colors mb-8 group">
                <BackIcon size={20} className="group-hover:-translate-x-1 rtl:group-hover:translate-x-1 transition-transform" />
                <span className="font-sans text-lg">{isRtl ? 'חזרה לתפריט' : 'Back to Menu'}</span>
            </Link>
        </FadeIn>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            
            {/* Image Section */}
            <FadeIn className="relative aspect-square lg:aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl border border-fait-brown/30 group">
                 <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10"></div>
                 <FaitImage 
                    src={getProductImage(item.id)}
                    alt={item.name}
                    imageContext={item.imageContext as any}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-1000"
                 />
                 
                 {/* Floating Badge */}
                 {item.bestSeller && (
                    <div className="absolute top-6 right-6 z-20 bg-fait-gold text-fait-dark font-bold px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
                        <Flame size={16} fill="currentColor" />
                        <span>{t.menu.bestSellerLabel}</span>
                    </div>
                 )}
            </FadeIn>

            {/* Content Section */}
            <div className="space-y-8">
                <FadeIn delay={100}>
                    <div className="flex justify-between items-start">
                        <h1 className="text-4xl md:text-6xl font-serif font-bold text-fait-gold mb-2">{item.name}</h1>
                        {item.price && (
                            <span className="text-2xl font-sans font-bold text-white bg-white/10 px-4 py-2 rounded-lg whitespace-nowrap">
                                {item.price}
                            </span>
                        )}
                    </div>
                    {item.isSpicy && (
                        <div className="flex items-center gap-2 text-red-400 mt-2 font-bold text-sm bg-red-500/10 inline-flex px-3 py-1 rounded-full border border-red-500/20">
                            <Flame size={14} /> {isRtl ? 'חריף' : 'Spicy'}
                        </div>
                    )}
                </FadeIn>

                <FadeIn delay={200}>
                    <p className="text-xl text-gray-300 leading-relaxed font-sans border-l-4 rtl:border-l-0 rtl:border-r-4 border-fait-gold pl-6 rtl:pl-0 rtl:pr-6">
                        {item.description}
                    </p>
                </FadeIn>

                {/* Extra Details Box */}
                <FadeIn delay={300}>
                    <div className="bg-[#1E1611] rounded-2xl p-8 border border-white/5">
                        
                        <div className="flex items-center gap-3 mb-6">
                            <Utensils className="text-fait-gold" />
                            <h3 className="text-xl font-bold text-white font-serif">{isRtl ? 'מה בצלחת?' : 'Inside the Dish'}</h3>
                        </div>

                        {extras.ingredients.length > 0 && (
                            <div className="mb-6">
                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {extras.ingredients.map((ing, i) => (
                                        <li key={i} className="flex items-center gap-2 text-gray-400">
                                            <CheckCircle2 size={16} className="text-fait-gold shrink-0" />
                                            <span>{ing}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="flex items-center gap-4 pt-6 border-t border-white/10">
                            <div className="flex items-center gap-2 text-fait-cream">
                                <Clock size={18} className="text-fait-gold" />
                                <span className="text-sm font-bold">{extras.prepTime}</span>
                            </div>
                        </div>
                        
                        <div className="mt-4 text-sm text-gray-500 italic">
                            "{extras.highlight}"
                        </div>
                    </div>
                </FadeIn>

                {/* CTA Buttons */}
                <FadeIn delay={400} className="pt-4 flex flex-col sm:flex-row gap-4">
                    <Link 
                        to="/contact" 
                        className="flex-1 bg-fait-gold text-fait-dark font-bold py-4 px-8 rounded-xl text-center hover:bg-white hover:scale-105 transition-all shadow-lg flex items-center justify-center gap-2"
                    >
                        <span>{isRtl ? 'בואו לטעום' : 'Come Taste'}</span>
                        <ArrowIcon size={20} />
                    </Link>
                     <Link 
                        to="/menu" 
                        className="flex-1 bg-transparent border border-white/20 text-white font-bold py-4 px-8 rounded-xl text-center hover:bg-white/5 transition-all"
                    >
                        {isRtl ? 'חזרה לתפריט' : 'Back to Menu'}
                    </Link>
                </FadeIn>

            </div>
        </div>
      </div>
    </div>
  );
};

export default MenuItemDetail;
